<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ContactSyncConfigurationAPI from 'dashboard/api/contactSyncConfiguration';

const props = defineProps({ customFields: { type: Array, default: () => [] } });
const emit = defineEmits(['create-contact-field']);
const { t } = useI18n();

const loading = ref(true);
const saving = ref(false);
const testing = ref(false);
const runningBaseSync = ref(false);
const previewingBaseSync = ref(false);
const sampleValue = ref('');
const testResult = ref(null);
const responsePreview = ref(null);
const baseSyncPreview = ref(null);
const mappingStarted = ref(false);
const mappingDialogRef = ref(null);
let baseSyncPollingTimer;
const config = ref({
  enabled: false,
  mode: 'per_conversation',
  url: '',
  http_method: 'post',
  request_parameter: 'phone',
  lookup_field: 'phone_number',
  lookup_rules: [{ field: 'phone_number', parameter: 'phone' }],
  conversation_trigger: 'customer',
  records_path: 'results',
  update_policy: 'fill_blank',
  authorization: '',
  authorization_configured: false,
  mappings: [],
  contact_display_fields: [],
  base_sync_pagination: { strategy: 'none' },
  base_sync_run: null,
  base_sync_runs: [],
  last_sync_status: {},
});

const lookupFields = computed(() => [
  { value: 'phone_number', label: t('CONTACT_SYNC.FIELD_PHONE') },
  { value: 'email', label: t('CONTACT_SYNC.FIELD_EMAIL') },
  { value: 'identifier', label: t('CONTACT_SYNC.FIELD_IDENTIFIER') },
  ...props.customFields.map(field => ({
    value: `custom_attributes.${field.attribute_key}`,
    label: field.attribute_display_name,
  })),
]);

const standardContactFieldKeys = new Set([
  'name',
  'email',
  'phone_number',
  'identifier',
  'city',
  'cidade',
  'country',
  'country_code',
  'pais',
  'description',
  'descricao',
  'biografia',
  'company_name',
  'empresa',
]);

const normalizeFieldKey = value =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const targetFields = computed(() => [
  { value: 'name', label: t('CONTACT_SYNC.FIELD_FULL_NAME') },
  { value: 'email', label: t('CONTACT_SYNC.FIELD_EMAIL') },
  { value: 'phone_number', label: t('CONTACT_SYNC.FIELD_PHONE') },
  { value: 'identifier', label: t('CONTACT_SYNC.FIELD_IDENTIFIER') },
  { value: 'additional_attributes.city', label: t('CONTACT_SYNC.FIELD_CITY') },
  {
    value: 'additional_attributes.country_code',
    label: t('CONTACT_SYNC.FIELD_COUNTRY'),
  },
  {
    value: 'additional_attributes.description',
    label: t('CONTACT_SYNC.FIELD_BIOGRAPHY'),
  },
  {
    value: 'additional_attributes.company_name',
    label: t('CONTACT_SYNC.FIELD_COMPANY'),
  },
  ...props.customFields
    .filter(
      field =>
        !standardContactFieldKeys.has(normalizeFieldKey(field.attribute_key))
    )
    .map(field => ({
      value: `custom_attributes.${field.attribute_key}`,
      label: field.attribute_display_name,
    })),
]);

const validMappingCount = computed(
  () =>
    config.value.mappings.filter(
      mapping => mapping.source?.trim() && mapping.target?.trim()
    ).length
);

const baseSyncRun = computed(() => config.value.base_sync_run);
const baseSyncActive = computed(() =>
  ['queued', 'running'].includes(baseSyncRun.value?.status)
);

const apiUrlIsValid = value => {
  try {
    const parsedUrl = new URL(value?.trim());
    return (
      ['http:', 'https:'].includes(parsedUrl.protocol) &&
      Boolean(parsedUrl.hostname)
    );
  } catch {
    return false;
  }
};

const validateApiUrl = () => {
  if (!config.value.enabled || apiUrlIsValid(config.value.url)) return true;

  useAlert(t('CONTACT_SYNC.ERROR_INVALID_API_URL'));
  return false;
};

const load = async () => {
  loading.value = true;
  try {
    const { data } = await ContactSyncConfigurationAPI.get();
    config.value = { ...config.value, ...data, authorization: '' };
    if (!config.value.lookup_rules?.length) {
      config.value.lookup_rules = [
        {
          field: config.value.lookup_field || 'phone_number',
          parameter: config.value.request_parameter || 'phone',
        },
      ];
    }
    const firstLookupRule = config.value.lookup_rules.find(
      rule => rule.field?.trim() && rule.parameter?.trim()
    );
    if (firstLookupRule) {
      config.value.lookup_field = firstLookupRule.field.trim();
      config.value.request_parameter = firstLookupRule.parameter.trim();
    }
    mappingStarted.value = config.value.mappings.length > 0;
  } catch {
    useAlert(t('CONTACT_SYNC.ERROR_LOAD'));
  } finally {
    loading.value = false;
  }
};

const stopBaseSyncPolling = () => {
  window.clearTimeout(baseSyncPollingTimer);
};

const scheduleBaseSyncPolling = () => {
  stopBaseSyncPolling();
  if (!baseSyncActive.value) return;

  baseSyncPollingTimer = window.setTimeout(async () => {
    await load();
    scheduleBaseSyncPolling();
  }, 5_000);
};

const save = async () => {
  if (!validateApiUrl()) return false;

  const invalidMappings = config.value.mappings.filter(
    mapping => !mapping.source?.trim() || !mapping.target?.trim()
  );
  if (invalidMappings.length) {
    useAlert(
      t('CONTACT_SYNC.ERROR_INCOMPLETE_MAPPINGS', {
        count: invalidMappings.length,
      })
    );
    return false;
  }

  if (config.value.mode === 'per_conversation') {
    const firstLookupRule = config.value.lookup_rules.find(
      rule => rule.field?.trim() && rule.parameter?.trim()
    );
    if (!firstLookupRule) {
      useAlert(t('CONTACT_SYNC.ERROR_LOOKUP_CRITERION'));
      return false;
    }
    config.value.lookup_field = firstLookupRule.field.trim();
    config.value.request_parameter = firstLookupRule.parameter.trim();
  }

  saving.value = true;
  try {
    const { data } = await ContactSyncConfigurationAPI.update(config.value);
    config.value = { ...config.value, ...data, authorization: '' };
    useAlert(t('CONTACT_SYNC.SUCCESS_SAVED'));
    return true;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('CONTACT_SYNC.ERROR_SAVE'));
    return false;
  } finally {
    saving.value = false;
  }
};

const responseFieldPaths = (record, prefix = '') => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return [];

  return Object.entries(record).flatMap(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    return value && typeof value === 'object' && !Array.isArray(value)
      ? responseFieldPaths(value, path)
      : [path];
  });
};

const sourceFields = computed(() => {
  const mappedSources = config.value.mappings.map(mapping => mapping.source);
  const responseSources = responseFieldPaths(responsePreview.value);
  return [...new Set([...responseSources, ...mappedSources])]
    .filter(Boolean)
    .map(source => ({ value: source, label: source }));
});

const normalizedSource = source =>
  normalizeFieldKey(source).replace(/[^a-z0-9]+/g, '_');

const suggestedTargetFor = source => {
  const sourceKey = normalizedSource(source);
  const exactCustomField = props.customFields.find(
    field => normalizeFieldKey(field.attribute_key) === sourceKey
  );
  if (exactCustomField)
    return `custom_attributes.${exactCustomField.attribute_key}`;

  if (
    ['name', 'full_name', 'razao_social', 'nome_fantasia'].includes(sourceKey)
  )
    return 'name';
  if (['email', 'email_cliente'].includes(sourceKey)) return 'email';
  if (
    [
      'phone',
      'phone_number',
      'telefone',
      'telefone_1',
      'celular',
      'cellphone',
    ].includes(sourceKey)
  )
    return 'phone_number';
  if (['identifier', 'id'].includes(sourceKey)) return 'identifier';

  return '';
};

const mapFields = async () => {
  if (!validateApiUrl()) return;

  if (!sampleValue.value.trim()) {
    useAlert(t('CONTACT_SYNC.ERROR_SAMPLE_REQUIRED'));
    return;
  }

  testing.value = true;
  testResult.value = null;
  try {
    const { data } = await ContactSyncConfigurationAPI.test(
      sampleValue.value,
      config.value
    );
    testResult.value = data;
    if (!data.success) return;

    responsePreview.value = data.preview || null;
    mappingStarted.value = true;

    const existingSources = new Set(
      config.value.mappings.map(mapping => mapping.source)
    );
    const fields = responseFieldPaths(data.preview).filter(
      source => !existingSources.has(source)
    );

    if (!fields.length) {
      useAlert(t('CONTACT_SYNC.NO_NEW_FIELDS'));
      return;
    }

    config.value.mappings.push(
      ...fields.map(source => ({ source, target: suggestedTargetFor(source) }))
    );
    useAlert(t('CONTACT_SYNC.SUCCESS_FIELDS_ADDED', { count: fields.length }));
  } catch (error) {
    testResult.value = {
      success: false,
      error:
        error?.response?.data?.error || t('CONTACT_SYNC.ERROR_QUERY_FIELDS'),
    };
  } finally {
    testing.value = false;
  }
};

const runBaseSync = async () => {
  runningBaseSync.value = true;
  try {
    const { data } = await ContactSyncConfigurationAPI.syncBase();
    useAlert(data.message);
    await load();
    scheduleBaseSyncPolling();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONTACT_SYNC.ERROR_START_IMPORT')
    );
  } finally {
    runningBaseSync.value = false;
  }
};

const previewBaseSync = async () => {
  previewingBaseSync.value = true;
  try {
    const saved = await save();
    if (!saved) return;
    const { data } = await ContactSyncConfigurationAPI.previewBase();
    baseSyncPreview.value = data;
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONTACT_SYNC.ERROR_PREVIEW_IMPORT')
    );
  } finally {
    previewingBaseSync.value = false;
  }
};

const pauseBaseSync = async () => {
  try {
    const { data } = await ContactSyncConfigurationAPI.pauseBase();
    useAlert(data.message);
    await load();
    scheduleBaseSyncPolling();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONTACT_SYNC.ERROR_PAUSE_IMPORT')
    );
  }
};

const resumeBaseSync = async () => {
  try {
    const { data } = await ContactSyncConfigurationAPI.resumeBase();
    useAlert(data.message);
    await load();
    scheduleBaseSyncPolling();
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONTACT_SYNC.ERROR_RESUME_IMPORT')
    );
  }
};

const addMapping = () => config.value.mappings.push({ source: '', target: '' });
const removeMapping = index => config.value.mappings.splice(index, 1);
const addDisplayField = () => config.value.contact_display_fields.push('');
const removeDisplayField = index =>
  config.value.contact_display_fields.splice(index, 1);
const addLookupRule = () =>
  config.value.lookup_rules.push({ field: 'phone_number', parameter: 'phone' });
const removeLookupRule = index => config.value.lookup_rules.splice(index, 1);
const openMappingDialog = () => mappingDialogRef.value?.open();

const saveMappings = async () => {
  const saved = await save();
  if (saved) mappingDialogRef.value?.close();
};

const handleSynchronizationToggle = async enabled => {
  if (!enabled) await save();
};

onMounted(async () => {
  await load();
  scheduleBaseSyncPolling();
});

onBeforeUnmount(stopBaseSyncPolling);
</script>

<template>
  <section class="p-5 mb-6 border rounded-xl border-n-weak bg-n-alpha-1">
    <div class="flex flex-wrap items-start justify-between gap-4 mb-5">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('CONTACT_SYNC.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('CONTACT_SYNC.DESCRIPTION') }}
        </p>
      </div>
      <label
        class="flex items-center gap-3 ml-auto text-sm font-medium whitespace-nowrap text-n-slate-12"
      >
        <span>{{ $t('CONTACT_SYNC.ENABLE') }}</span>
        <Switch
          v-model="config.enabled"
          :disabled="loading"
          @change="handleSynchronizationToggle"
        />
      </label>
    </div>

    <div v-if="config.enabled" class="mt-5">
      <div class="space-y-4">
        <section class="p-4 border rounded-xl border-n-weak bg-n-alpha-black1">
          <div class="flex items-start gap-3 mb-4">
            <span
              class="flex items-center justify-center text-sm font-semibold rounded-full size-7 bg-n-brand/15 text-n-brand"
              >{{ $t('CONTACT_SYNC.STEP_CONNECTION') }}</span
            >
            <div>
              <h3 class="font-medium text-n-slate-12">
                {{ $t('CONTACT_SYNC.API_CONNECTION') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CONTACT_SYNC.API_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label class="text-sm font-medium text-n-slate-12"
              >{{ $t('CONTACT_SYNC.MODE') }}
              <div class="relative mt-1">
                <select
                  v-model="config.mode"
                  class="w-full h-10 px-3 pr-10 rounded-lg appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
                >
                  <option value="per_conversation">
                    {{ $t('CONTACT_SYNC.PER_CONVERSATION') }}
                  </option>
                  <option value="base">
                    {{ $t('CONTACT_SYNC.FULL_BASE') }}
                  </option>
                </select>
                <span
                  class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-10"
                />
              </div>
            </label>
            <label class="text-sm font-medium text-n-slate-12"
              >{{ $t('CONTACT_SYNC.HTTP_METHOD') }}
              <div class="relative mt-1">
                <select
                  v-model="config.http_method"
                  class="w-full h-10 px-3 pr-10 rounded-lg appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
                >
                  <option value="post">{{ $t('CONTACT_SYNC.POST') }}</option>
                  <option value="get">{{ $t('CONTACT_SYNC.GET') }}</option>
                </select>
                <span
                  class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-10"
                />
              </div>
            </label>
            <label class="text-sm font-medium text-n-slate-12 md:col-span-2"
              >{{ $t('CONTACT_SYNC.API_URL') }}
              <input
                v-model="config.url"
                type="url"
                inputmode="url"
                autocomplete="url"
                :placeholder="$t('CONTACT_SYNC.API_URL_PLACEHOLDER')"
                class="w-full h-10 px-3 mt-1 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak"
              />
            </label>
            <label class="text-sm font-medium text-n-slate-12 md:col-span-2"
              >{{ $t('CONTACT_SYNC.AUTHORIZATION') }}
              <input
                v-model="config.authorization"
                type="password"
                :placeholder="
                  config.authorization_configured
                    ? $t('CONTACT_SYNC.SAVED_TOKEN_PLACEHOLDER')
                    : $t('CONTACT_SYNC.BEARER_PLACEHOLDER')
                "
                class="w-full h-10 px-3 mt-1 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak"
              />
            </label>
          </div>
        </section>

        <section
          v-if="config.mode === 'per_conversation'"
          class="p-4 border rounded-xl border-n-weak bg-n-alpha-black1"
        >
          <div class="flex items-start gap-3 mb-4">
            <span
              class="flex items-center justify-center text-sm font-semibold rounded-full size-7 bg-n-brand/15 text-n-brand"
              >{{ $t('CONTACT_SYNC.STEP_LOOKUP') }}</span
            >
            <div>
              <h3 class="font-medium text-n-slate-12">
                {{ $t('CONTACT_SYNC.AUTO_LOOKUP') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CONTACT_SYNC.AUTO_LOOKUP_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <label class="text-sm font-medium text-n-slate-12 md:col-span-2"
            >{{ $t('CONTACT_SYNC.TRIGGER') }}
            <div class="relative mt-1">
              <select
                v-model="config.conversation_trigger"
                class="w-full h-10 px-3 pr-10 rounded-lg appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
              >
                <option value="customer">
                  {{ $t('CONTACT_SYNC.CUSTOMER_STARTS') }}
                </option>
                <option value="agent">
                  {{ $t('CONTACT_SYNC.AGENT_STARTS') }}
                </option>
                <option value="both">
                  {{ $t('CONTACT_SYNC.BOTH_START') }}
                </option>
              </select>
              <span
                class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-10"
              />
            </div>
            <span class="block mt-1 text-xs font-normal text-n-slate-11">{{
              $t('CONTACT_SYNC.DELAY_DESCRIPTION')
            }}</span>
          </label>
          <div class="md:col-span-2">
            <div class="flex flex-wrap items-start justify-between gap-2 mb-2">
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ $t('CONTACT_SYNC.LOOKUP_ATTEMPTS') }}
                </p>
                <p class="text-xs text-n-slate-11">
                  {{ $t('CONTACT_SYNC.LOOKUP_DESCRIPTION') }}
                </p>
              </div>
              <Button
                :label="$t('CONTACT_SYNC.ADD_CRITERION')"
                size="sm"
                variant="outline"
                icon="i-lucide-plus"
                @click="addLookupRule"
              />
            </div>
            <div class="space-y-3">
              <div
                v-for="(rule, index) in config.lookup_rules"
                :key="index"
                class="grid items-start grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto]"
              >
                <label class="text-sm font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.CONTACT_FIELD') }}
                  <div class="relative mt-1">
                    <select
                      v-model="rule.field"
                      class="w-full h-10 px-3 pr-10 rounded-lg appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
                    >
                      <option
                        v-for="field in lookupFields"
                        :key="field.value"
                        :value="field.value"
                      >
                        {{ field.label }}
                      </option></select
                    ><span
                      class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-10"
                    />
                  </div>
                </label>
                <label class="text-sm font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.API_PARAMETER') }}
                  <input
                    v-model="rule.parameter"
                    :placeholder="$t('CONTACT_SYNC.PARAMETER_PLACEHOLDER')"
                    class="w-full h-10 px-3 mt-1 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak"
                  />
                </label>
                <button
                  v-if="config.lookup_rules.length > 1"
                  type="button"
                  class="h-10 px-3 mt-7 text-sm text-red-500"
                  @click="removeLookupRule(index)"
                >
                  {{ $t('CONTACT_SYNC.REMOVE') }}
                </button>
              </div>
            </div>
            <p class="mt-2 text-xs text-n-slate-11">
              {{ $t('CONTACT_SYNC.EXAMPLES_PREFIX') }}
              {{ $t('CONTACT_SYNC.EXAMPLES') }}
            </p>
          </div>
        </section>

        <section class="p-4 border rounded-xl border-n-weak bg-n-alpha-black1">
          <div class="flex items-start gap-3 mb-4">
            <span
              class="flex items-center justify-center text-sm font-semibold rounded-full size-7 bg-n-brand/15 text-n-brand"
              >{{ $t('CONTACT_SYNC.STEP_UPDATE') }}</span
            >
            <div>
              <h3 class="font-medium text-n-slate-12">
                {{ $t('CONTACT_SYNC.RESPONSE_UPDATE') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CONTACT_SYNC.RESPONSE_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label class="text-sm font-medium text-n-slate-12"
              >{{ $t('CONTACT_SYNC.RECORDS_PATH') }}
              <input
                v-model="config.records_path"
                :placeholder="$t('CONTACT_SYNC.RECORDS_PATH_PLACEHOLDER')"
                class="w-full h-10 px-3 mt-1 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak"
              />
              <span class="block mt-1 text-xs font-normal text-n-slate-11">{{
                $t('CONTACT_SYNC.RECORDS_PATH_DESCRIPTION')
              }}</span>
            </label>
            <label class="text-sm font-medium text-n-slate-12"
              >{{ $t('CONTACT_SYNC.UPDATE_POLICY') }}
              <div class="relative mt-1">
                <select
                  v-model="config.update_policy"
                  class="w-full h-10 px-3 pr-10 rounded-lg appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
                >
                  <option value="fill_blank">
                    {{ $t('CONTACT_SYNC.FILL_BLANK') }}
                  </option>
                  <option value="overwrite">
                    {{ $t('CONTACT_SYNC.OVERWRITE') }}
                  </option>
                </select>
                <span
                  class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-10"
                />
              </div>
            </label>
            <div
              v-if="config.mode === 'base'"
              class="p-4 rounded-lg md:col-span-2 bg-n-alpha-black2"
            >
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h4 class="text-sm font-medium text-n-slate-12">
                    {{ $t('CONTACT_SYNC.BATCH_IMPORT') }}
                  </h4>
                  <p class="mt-1 text-xs text-n-slate-11">
                    {{ $t('CONTACT_SYNC.BATCH_DESCRIPTION') }}
                  </p>
                </div>
                <span class="text-xs font-medium text-n-brand">{{
                  $t('CONTACT_SYNC.BATCH_RECOMMENDED')
                }}</span>
              </div>
              <label class="block mt-4 text-sm font-medium text-n-slate-12"
                >{{ $t('CONTACT_SYNC.PAGINATION_STRATEGY') }}
                <div class="relative mt-1">
                  <select
                    v-model="config.base_sync_pagination.strategy"
                    class="w-full h-10 px-3 pr-10 rounded-lg appearance-none bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  >
                    <option value="page">
                      {{ $t('CONTACT_SYNC.PAGE_SIZE') }}
                    </option>
                    <option value="cursor">
                      {{ $t('CONTACT_SYNC.CURSOR') }}
                    </option>
                    <option value="none">
                      {{ $t('CONTACT_SYNC.SINGLE_RESPONSE') }}
                    </option>
                  </select>
                  <span
                    class="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 i-lucide-chevron-down text-n-slate-10"
                  />
                </div>
              </label>
              <div
                v-if="config.base_sync_pagination.strategy === 'page'"
                class="grid grid-cols-1 gap-3 mt-4 md:grid-cols-4"
              >
                <label class="text-xs font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.PAGE_PARAMETER') }}
                  <input
                    v-model="config.base_sync_pagination.page_parameter"
                    :placeholder="$t('CONTACT_SYNC.PAGE_PARAMETER_PLACEHOLDER')"
                    class="w-full h-9 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  />
                </label>
                <label class="text-xs font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.SIZE_PARAMETER') }}
                  <input
                    v-model="config.base_sync_pagination.page_size_parameter"
                    :placeholder="$t('CONTACT_SYNC.SIZE_PARAMETER_PLACEHOLDER')"
                    class="w-full h-9 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  />
                </label>
                <label class="text-xs font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.RECORDS_PER_BATCH') }}
                  <input
                    v-model.number="config.base_sync_pagination.page_size"
                    min="1"
                    max="1000"
                    type="number"
                    :placeholder="$t('CONTACT_SYNC.RECORDS_PLACEHOLDER')"
                    class="w-full h-9 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  />
                </label>
                <label class="text-xs font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.BATCH_DELAY') }}
                  <input
                    v-model.number="config.base_sync_pagination.delay_seconds"
                    min="0.5"
                    max="60"
                    step="0.5"
                    type="number"
                    :placeholder="$t('CONTACT_SYNC.DELAY_PLACEHOLDER')"
                    class="w-full h-9 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  />
                </label>
              </div>
              <div
                v-else-if="config.base_sync_pagination.strategy === 'cursor'"
                class="grid grid-cols-1 gap-3 mt-4 md:grid-cols-3"
              >
                <label class="text-xs font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.CURSOR_PARAMETER') }}
                  <input
                    v-model="config.base_sync_pagination.cursor_parameter"
                    :placeholder="$t('CONTACT_SYNC.CURSOR_PLACEHOLDER')"
                    class="w-full h-9 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  />
                </label>
                <label class="text-xs font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.NEXT_CURSOR_PATH') }}
                  <input
                    v-model="config.base_sync_pagination.next_cursor_path"
                    :placeholder="$t('CONTACT_SYNC.NEXT_CURSOR_PLACEHOLDER')"
                    class="w-full h-9 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  />
                </label>
                <label class="text-xs font-medium text-n-slate-12"
                  >{{ $t('CONTACT_SYNC.BATCH_DELAY') }}
                  <input
                    v-model.number="config.base_sync_pagination.delay_seconds"
                    min="0.5"
                    max="60"
                    step="0.5"
                    type="number"
                    :placeholder="$t('CONTACT_SYNC.DELAY_PLACEHOLDER')"
                    class="w-full h-9 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
                  />
                </label>
              </div>
              <p v-else class="mt-3 text-xs text-amber-600">
                {{ $t('CONTACT_SYNC.SINGLE_RESPONSE_WARNING') }}
              </p>
            </div>
          </div>
        </section>
      </div>

      <section
        class="p-4 mt-4 border rounded-xl border-n-weak bg-n-alpha-black1"
      >
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="flex items-start gap-3">
            <span
              class="flex items-center justify-center text-sm font-semibold rounded-full size-7 bg-n-brand/15 text-n-brand"
              >{{ $t('CONTACT_SYNC.STEP_MAPPING') }}</span
            >
            <div>
              <h3 class="font-medium text-n-slate-12">
                {{ $t('CONTACT_SYNC.MAPPING') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CONTACT_SYNC.MAPPING_DESCRIPTION') }}
              </p>
              <p
                class="mt-1 text-xs font-medium"
                :class="
                  validMappingCount === config.mappings.length &&
                  config.mappings.length
                    ? 'text-green-600'
                    : 'text-amber-600'
                "
              >
                {{
                  validMappingCount === config.mappings.length &&
                  config.mappings.length
                    ? `${validMappingCount} campo(s) da API mapeado(s)`
                    : `${validMappingCount} de ${config.mappings.length} campo(s) da API completo(s)`
                }}
              </p>
            </div>
          </div>
          <Button
            :label="$t('CONTACT_SYNC.MAP_FIELDS')"
            variant="outline"
            icon="i-lucide-map"
            @click="openMappingDialog"
          />
        </div>
        <p
          v-if="
            config.enabled &&
            config.mode === 'per_conversation' &&
            !mappingStarted
          "
          class="mt-3 text-sm text-amber-600"
        >
          {{ $t('CONTACT_SYNC.MAPPING_REQUIRED') }}
        </p>
      </section>

      <section
        class="p-4 mt-4 border rounded-xl border-n-weak bg-n-alpha-black1"
      >
        <div class="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div class="flex items-start gap-3">
            <span
              class="flex items-center justify-center text-sm font-semibold rounded-full size-7 bg-n-brand/15 text-n-brand"
              >{{ $t('CONTACT_SYNC.STEP_PRESENTATION') }}</span
            >
            <div>
              <h3 class="font-medium text-n-slate-12">
                {{ $t('CONTACT_SYNC.PRESENTATION') }}
              </h3>
              <p class="text-sm text-n-slate-11">
                {{ $t('CONTACT_SYNC.PRESENTATION_DESCRIPTION') }}
              </p>
            </div>
          </div>
          <Button
            :label="$t('CONTACT_SYNC.ADD_FIELD')"
            size="sm"
            variant="outline"
            icon="i-lucide-plus"
            @click="addDisplayField"
          />
        </div>
        <div v-if="config.contact_display_fields.length" class="space-y-2">
          <div
            v-for="(_, index) in config.contact_display_fields"
            :key="index"
            class="grid items-center grid-cols-1 gap-3 md:grid-cols-[1fr_auto]"
          >
            <label class="text-xs text-n-slate-11">
              <span>{{
                $t('CONTACT_SYNC.FIELD_NUMBER', { number: index + 1 })
              }}</span>
              <span class="relative flex h-9 mt-1">
                <select
                  v-model="config.contact_display_fields[index]"
                  class="w-full h-9 px-2 pr-8 rounded appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
                >
                  <option disabled value="">
                    {{ $t('CONTACT_SYNC.SELECT_FIELD') }}
                  </option>
                  <option
                    v-for="field in targetFields"
                    :key="field.value"
                    :value="field.value"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <span
                  class="pointer-events-none absolute inset-y-0 right-2 flex items-center"
                  ><span class="size-4 i-lucide-chevron-down text-n-slate-10"
                /></span>
              </span>
            </label>
            <button
              type="button"
              class="h-9 px-3 mt-4 text-sm text-red-500"
              @click="removeDisplayField(index)"
            >
              {{ $t('CONTACT_SYNC.REMOVE') }}
            </button>
          </div>
          <p class="text-xs text-n-slate-11">
            {{ $t('CONTACT_SYNC.PRESENTATION_EXAMPLE') }}
          </p>
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ $t('CONTACT_SYNC.NO_DISPLAY_FIELDS') }}
        </p>
      </section>

      <div
        class="flex flex-wrap items-center gap-3 pt-5 mt-5 border-t border-n-weak"
      >
        <Button
          :label="$t('CONTACT_SYNC.SAVE_CONFIGURATION')"
          :is-loading="saving"
          @click="save"
        />
        <template v-if="config.mode === 'base'">
          <Button
            :label="$t('CONTACT_SYNC.PREVIEW_BATCH')"
            variant="outline"
            :is-loading="previewingBaseSync"
            :disabled="baseSyncActive"
            @click="previewBaseSync"
          />
          <Button
            v-if="baseSyncRun?.status === 'paused'"
            :label="$t('CONTACT_SYNC.RESUME_IMPORT')"
            variant="outline"
            @click="resumeBaseSync"
          />
          <Button
            v-else-if="baseSyncActive"
            :label="$t('CONTACT_SYNC.PAUSE_IMPORT')"
            variant="outline"
            color="slate"
            @click="pauseBaseSync"
          />
          <Button
            v-else
            :label="$t('CONTACT_SYNC.RUN_IMPORT')"
            variant="outline"
            :is-loading="runningBaseSync"
            @click="runBaseSync"
          />
        </template>
      </div>
      <div
        v-if="baseSyncPreview"
        class="p-3 mt-4 text-sm rounded-lg bg-n-alpha-black1"
      >
        <p class="font-medium text-n-slate-12">
          {{ $t('CONTACT_SYNC.PREVIEW_TITLE') }}
        </p>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('CONTACT_SYNC.PREVIEW_COUNTS', baseSyncPreview) }}
        </p>
      </div>
      <div
        v-if="config.mode === 'base' && baseSyncRun"
        class="p-3 mt-4 text-sm rounded-lg bg-n-alpha-black1"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span class="font-medium text-n-slate-12"
            >{{ $t('CONTACT_SYNC.IMPORT') }}
            {{
              baseSyncRun.status === 'completed'
                ? $t('CONTACT_SYNC.COMPLETED')
                : baseSyncRun.status === 'failed'
                  ? $t('CONTACT_SYNC.INTERRUPTED')
                  : $t('CONTACT_SYNC.IN_PROGRESS')
            }}</span
          >
          <span class="text-xs text-n-slate-11">{{
            $t('CONTACT_SYNC.PAGES', { count: baseSyncRun.pages || 0 })
          }}</span>
        </div>
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('CONTACT_SYNC.RUN_COUNTS', baseSyncRun) }}
        </p>
        <p v-if="baseSyncRun.error_message" class="mt-2 text-xs text-red-500">
          {{ baseSyncRun.error_message }}
        </p>
      </div>
      <div
        v-if="config.mode === 'base' && config.base_sync_runs?.length"
        class="mt-4"
      >
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ $t('CONTACT_SYNC.LATEST_IMPORTS') }}
        </h4>
        <div class="mt-2 overflow-hidden border rounded-lg border-n-weak">
          <div
            v-for="run in config.base_sync_runs"
            :key="`${run.created_at}-${run.sequence}`"
            class="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs border-b last:border-b-0 border-n-weak"
          >
            <span class="font-medium text-n-slate-12">{{
              run.status === 'completed'
                ? $t('CONTACT_SYNC.COMPLETED_FEMININE')
                : run.status === 'paused'
                  ? $t('CONTACT_SYNC.PAUSED')
                  : run.status === 'failed'
                    ? $t('CONTACT_SYNC.INTERRUPTED_FEMININE')
                    : $t('CONTACT_SYNC.IN_PROGRESS')
            }}</span>
            <span class="text-n-slate-11">{{
              $t('CONTACT_SYNC.RUN_COUNTS', run)
            }}</span>
          </div>
        </div>
      </div>
      <p
        v-if="config.last_sync_status?.message"
        class="mt-4 text-sm text-n-slate-11"
      >
        {{
          $t('CONTACT_SYNC.LAST_RESULT', {
            message: config.last_sync_status.message,
          })
        }}
      </p>
    </div>

    <p
      v-else
      class="px-4 py-3 mt-5 text-sm rounded-lg bg-n-alpha-black1 text-n-slate-11"
    >
      {{ $t('CONTACT_SYNC.DISABLED_DESCRIPTION') }}
    </p>

    <Dialog
      ref="mappingDialogRef"
      type="edit"
      width="4xl"
      :title="$t('CONTACT_SYNC.MAPPING')"
      :description="$t('CONTACT_SYNC.MAPPING_DIALOG_DESCRIPTION')"
      :show-cancel-button="false"
      :show-confirm-button="false"
      overflow-y-auto
    >
      <div class="space-y-5">
        <div
          class="flex flex-wrap items-end gap-3 p-4 rounded-lg bg-n-alpha-black2"
        >
          <label class="flex-1 min-w-60 text-sm font-medium text-n-slate-12"
            >{{ $t('CONTACT_SYNC.SAMPLE_VALUE') }}
            <input
              v-model="sampleValue"
              :placeholder="$t('CONTACT_SYNC.SAMPLE_PLACEHOLDER')"
              class="w-full h-10 px-3 mt-1 rounded-lg bg-n-alpha-black1 outline outline-1 outline-n-weak"
            />
          </label>
          <Button
            :label="$t('CONTACT_SYNC.QUERY_FIELDS')"
            :is-loading="testing"
            icon="i-lucide-search"
            @click="mapFields"
          />
        </div>

        <p
          v-if="testResult"
          class="text-sm"
          :class="testResult.success ? 'text-green-600' : 'text-red-500'"
        >
          {{
            testResult.success
              ? $t('CONTACT_SYNC.QUERY_COMPLETED', {
                  count: testResult.records_found,
                })
              : testResult.error
          }}
        </p>

        <details
          v-if="responsePreview"
          open
          class="p-3 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-11"
        >
          <summary class="font-medium cursor-pointer text-n-slate-12">
            {{ $t('CONTACT_SYNC.VIEW_RESPONSE') }}
          </summary>
          <pre
            class="max-h-56 mt-3 overflow-auto text-xs whitespace-pre-wrap"
            >{{ JSON.stringify(responsePreview, null, 2) }}</pre
          >
        </details>

        <div class="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h4 class="font-medium text-n-slate-12">
              {{ $t('CONTACT_SYNC.MAPPED_FIELDS') }}
            </h4>
            <p class="text-sm text-n-slate-11">
              {{ $t('CONTACT_SYNC.MAPPED_FIELDS_DESCRIPTION') }}
            </p>
          </div>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="$t('CONTACT_SYNC.CREATE_CUSTOM_FIELD')"
              size="sm"
              variant="outline"
              icon="i-lucide-plus"
              @click="emit('create-contact-field')"
            />
            <Button
              :label="$t('CONTACT_SYNC.ADD_FIELD')"
              size="sm"
              icon="i-lucide-plus"
              @click="addMapping"
            />
          </div>
        </div>

        <div v-if="config.mappings.length" class="space-y-3">
          <div
            v-for="(mapping, index) in config.mappings"
            :key="index"
            class="grid items-start grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto]"
          >
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              <span>{{ $t('CONTACT_SYNC.RECEIVED_FIELD') }}</span>
              <span class="relative flex h-9">
                <select
                  v-model="mapping.source"
                  class="w-full h-9 px-2 pr-8 rounded appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
                >
                  <option disabled value="">
                    {{ $t('CONTACT_SYNC.SELECT_RETURNED_FIELD') }}
                  </option>
                  <option
                    v-for="field in sourceFields"
                    :key="field.value"
                    :value="field.value"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <span
                  class="pointer-events-none absolute inset-y-0 right-2 flex items-center"
                  ><span class="size-4 i-lucide-chevron-down text-n-slate-10"
                /></span>
              </span>
            </label>
            <label class="flex flex-col gap-1 text-xs text-n-slate-11">
              <span>{{ $t('CONTACT_SYNC.SAVE_TO') }}</span>
              <span class="relative flex h-9">
                <select
                  v-model="mapping.target"
                  class="w-full h-9 px-2 pr-8 rounded appearance-none bg-n-alpha-black2 outline outline-1 outline-n-weak"
                >
                  <option disabled value="">
                    {{ $t('CONTACT_SYNC.SELECT_FIELD') }}
                  </option>
                  <option
                    v-for="field in targetFields"
                    :key="field.value"
                    :value="field.value"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <span
                  class="pointer-events-none absolute inset-y-0 right-2 flex items-center"
                  ><span class="size-4 i-lucide-chevron-down text-n-slate-10"
                /></span>
              </span>
            </label>
            <button
              type="button"
              class="h-9 px-3 mt-5 text-sm text-red-500"
              @click="removeMapping(index)"
            >
              {{ $t('CONTACT_SYNC.REMOVE') }}
            </button>
          </div>
        </div>
        <p v-else class="text-sm text-n-slate-11">
          {{ $t('CONTACT_SYNC.EMPTY_MAPPING') }}
        </p>

        <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
          <Button
            :label="$t('COMMON.CLOSE')"
            variant="faded"
            color="slate"
            @click="mappingDialogRef?.close()"
          />
          <Button
            :label="$t('CONTACT_SYNC.SAVE_MAPPING')"
            :is-loading="saving"
            @click="saveMappings"
          />
        </div>
      </div>
      <template #footer />
    </Dialog>
  </section>
</template>
